import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { 
  LogOut, 
  Car, 
  Calendar, 
  CheckCircle,
  Clock,
  Euro,
  Plus,
  Edit,
  Trash2,
  X,
  Percent,
  Save,
  CreditCard,
  Banknote,
  MessageSquare,
  Mail,
  FileText,
  ExternalLink,
  Pencil,
  Trash,
  Upload,
  Image,
  LayoutDashboard,
  Users,
  Receipt,
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  Menu,
  AlertTriangle,
  UserCheck
} from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { AdminRezervimet } from "@/components/admin/AdminRezervimet";
import { AdminKlientet } from "@/components/admin/AdminKlientet";
import { AdminFlota } from "@/components/admin/AdminFlota";
import { AdminFaturimi } from "@/components/admin/AdminFaturimi";
import { AdminKalendari } from "@/components/admin/AdminKalendari";
import { AdminGjobat } from "@/components/admin/AdminGjobat";
import { AdminPunonjesit } from "@/components/admin/AdminPunonjesit";
import { AdminNotifications } from "@/components/admin/AdminNotifications";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const AdminDashboard = () => {
  const { t } = useLanguage();
  const [stats, setStats] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [cars, setCars] = useState([]);
  const [discounts, setDiscounts] = useState([]);

  // Car-specific discount tiers (ranges)
  const [carDiscounts, setCarDiscounts] = useState([]);
  const [selectedDiscountCarId, setSelectedDiscountCarId] = useState("");
  const [carDiscountForm, setCarDiscountForm] = useState({ min_days: "", max_days: "", discount_per_day: "" });
  const [messages, setMessages] = useState([]);
  const [clients, setClients] = useState([]);
  const [userRole, setUserRole] = useState("admin");
  const [userName, setUserName] = useState("Admin");
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("stats");
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [showCarModal, setShowCarModal] = useState(false);
  const [editingCar, setEditingCar] = useState(null);
  const [showDiscountModal, setShowDiscountModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [depositAmount, setDepositAmount] = useState("");

  // Document preview modal
  const [showDocumentModal, setShowDocumentModal] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);

  // Monthly revenue state
  const [monthlyRevenue, setMonthlyRevenue] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState(new Date().toISOString().slice(0, 7));
  const [monthCars, setMonthCars] = useState([]);

  const loadMonthlyRevenue = async () => {
    try {
      const token = localStorage.getItem("adminToken");
      const response = await axios.get(`${API}/admin/revenue/monthly`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMonthlyRevenue(response.data || []);
    } catch (error) {
      console.error("Error loading monthly revenue:", error);
    }
  };

  const loadMonthCars = async (month) => {
    try {
      const token = localStorage.getItem("adminToken");
      const response = await axios.get(`${API}/admin/reservations/by-month?month=${month}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMonthCars(response.data?.cars || []);
    } catch (error) {
      console.error("Error loading month cars:", error);
    }
  };

  useEffect(() => {
    if (activeTab === "stats") loadMonthCars(selectedMonth);
  }, [selectedMonth, activeTab]);

  // Edit total modal
  const [showEditTotalModal, setShowEditTotalModal] = useState(false);
  const [editTotalValue, setEditTotalValue] = useState("");
  const [editingTotalBooking, setEditingTotalBooking] = useState(null);

  // Image upload state
  const [imageUploading, setImageUploading] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  
  // Gallery state
  const [galleryImages, setGalleryImages] = useState([]);
  const [galleryUploading, setGalleryUploading] = useState(false);

  const navigate = useNavigate();

  // Car form state
  const [carForm, setCarForm] = useState({
    brand: "",
    model: "",
    year: new Date().getFullYear(),
    price_per_day: "",
    high_season_price_per_day: "",
    min_rental_days: 1,
    tpl_price_per_day: 0,
    mini_kasko_price_per_day: 0,
    full_kasko_price_per_day: 0,
    fuel_type: "Benzinë",
    transmission: "Automatik",
    seats: 5,
    image_url: "",
    description: "",
    available: true
  });

  // Price tiers for the car being edited
  const [editPriceTiers, setEditPriceTiers] = useState([]);
  const [newTier, setNewTier] = useState({ min_days: "", max_days: "", price_per_day: "" });

  // Discount form state (legacy global discounts)
  const [discountForm, setDiscountForm] = useState({
    min_days: "",
    discount_percent: "",
    description: ""
  });

  const loadCarDiscounts = async (carId) => {
    if (!carId) {
      setCarDiscounts([]);
      return;
    }
    try {
      const token = localStorage.getItem("adminToken");
      const response = await axios.get(`${API}/admin/car-discounts?car_id=${carId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCarDiscounts(response.data.discounts || []);
    } catch (error) {
      console.error("Error loading car discounts:", error);
      setCarDiscounts([]);
    }
  };

  const createCarDiscount = async () => {
    if (!selectedDiscountCarId) return;

    const minDays = parseInt(carDiscountForm.min_days);
    const maxDays = carDiscountForm.max_days === "" ? null : parseInt(carDiscountForm.max_days);
    const perDay = parseFloat(carDiscountForm.discount_per_day);

    if (!minDays || minDays < 1 || !perDay || perDay < 0) {
      toast.error(t('admin_discount_failed') || 'Gabim në ulje');
      return;
    }

    try {
      const token = localStorage.getItem("adminToken");
      await axios.post(`${API}/admin/car-discounts`, {
        car_id: selectedDiscountCarId,
        min_days: minDays,
        max_days: maxDays,
        discount_per_day: perDay
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success(t('admin_discount_added') || 'Ulja u shtua');
      setCarDiscountForm({ min_days: "", max_days: "", discount_per_day: "" });
      loadCarDiscounts(selectedDiscountCarId);
    } catch (error) {
      console.error("Error creating car discount:", error);
      toast.error(t('admin_discount_failed') || 'Gabim në ulje');
    }
  };

  const deleteCarDiscount = async (discountId) => {
    try {
      const token = localStorage.getItem("adminToken");
      await axios.delete(`${API}/admin/car-discounts/${discountId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(t('admin_discount_deleted') || 'Ulja u fshi');
      loadCarDiscounts(selectedDiscountCarId);
    } catch (error) {
      console.error("Error deleting car discount:", error);
      toast.error(t('admin_discount_failed') || 'Gabim në ulje');
    }
  };

  useEffect(() => {
    verifyAndLoadData();
  }, []);

  const verifyAndLoadData = async () => {
    const token = localStorage.getItem("adminToken");
    if (!token) {
      navigate("/admin/login");
      return;
    }

    try {
      await axios.get(`${API}/admin/verify`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      await Promise.all([
        loadStats(),
        loadBookings(),
        loadCars(),
        loadDiscounts(),
        loadMessages(),
        loadClients(),
        checkUserRole(),
        loadMonthlyRevenue()
      ]);
    } catch (error) {
      console.error("Verification error:", error);
      localStorage.removeItem("adminToken");
      navigate("/admin/login");
    } finally {
      setLoading(false);
    }
  };

  const loadMessages = async () => {
    try {
      const token = localStorage.getItem("adminToken");
      const response = await axios.get(`${API}/admin/messages`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessages(response.data);
    } catch (error) {
      console.error("Error loading messages:", error);
    }
  };

  const deleteMessage = async (messageId) => {
    if (!window.confirm(t('admin_delete_message_confirm'))) return;
    
    try {
      const token = localStorage.getItem("adminToken");
      await axios.delete(`${API}/admin/messages/${messageId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(t('admin_message_deleted'));
      loadMessages();
    } catch (error) {
      console.error("Error deleting message:", error);
      toast.error(t('admin_message_delete_error'));
    }
  };

  const loadStats = async () => {
    try {
      const token = localStorage.getItem("adminToken");
      const response = await axios.get(`${API}/admin/dashboard/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      console.error("Error loading stats:", error);
    }
  };

  const loadBookings = async () => {
    try {
      const token = localStorage.getItem("adminToken");
      const response = await axios.get(`${API}/admin/bookings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBookings(response.data);
    } catch (error) {
      console.error("Error loading bookings:", error);
    }
  };

  const loadCars = async () => {
    try {
      const token = localStorage.getItem("adminToken");
      const response = await axios.get(`${API}/admin/cars`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const sortedCars = [...(response.data || [])].sort((a, b) => (Number(a.price_per_day) || 0) - (Number(b.price_per_day) || 0));
      setCars(sortedCars);
    } catch (error) {
      console.error("Error loading cars:", error);
    }
  };

  const loadDiscounts = async () => {
    try {
      const token = localStorage.getItem("adminToken");
      const response = await axios.get(`${API}/admin/discounts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDiscounts(response.data.discounts || []);
    } catch (error) {
      console.error("Error loading discounts:", error);
    }
  };

  const loadClients = async () => {
    try {
      const token = localStorage.getItem("adminToken");
      const response = await axios.get(`${API}/admin/clients`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setClients(response.data || []);
    } catch (error) {
      console.error("Error loading clients:", error);
    }
  };

  const checkUserRole = async () => {
    try {
      const token = localStorage.getItem("adminToken");
      const response = await axios.get(`${API}/admin/verify`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUserRole(response.data.role || "admin");
      setUserName(response.data.username || "Admin");
    } catch (error) {
      console.error("Error checking role:", error);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("adminToken");
    toast.success(t('admin_logout_success'));
    navigate("/admin/login");
  };

  const updateBookingStatus = async (bookingId, status) => {
    try {
      const token = localStorage.getItem("adminToken");
      await axios.put(
        `${API}/admin/bookings/${bookingId}/status?status=${status}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(t('admin_status_updated'));
      loadBookings();
      loadStats();
      loadCars();
    } catch (error) {
      console.error("Error updating status:", error);
      toast.error(t('admin_status_error'));
    }
  };

  // Payment functions
  const openPaymentModal = (booking) => {
    setSelectedBooking(booking);
    setDepositAmount(booking.deposit_amount || "");
    setShowPaymentModal(true);
  };

  const updatePaymentStatus = async (paymentStatus, amount = 0) => {
    if (!selectedBooking) return;
    
    try {
      const token = localStorage.getItem("adminToken");
      await axios.put(
        `${API}/admin/bookings/${selectedBooking.id}/payment?payment_status=${paymentStatus}&amount=${amount}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(
        paymentStatus === 'paid' ? t('admin_paid_fully') :
        paymentStatus === 'deposit' ? `${t('admin_deposit')} €${amount} ${t('admin_deposit_registered')}` :
        t('admin_payment_removed')
      );
      setShowPaymentModal(false);
      setSelectedBooking(null);
      setDepositAmount("");
      loadBookings();
      loadStats();
    } catch (error) {
      console.error("Error updating payment:", error);
      toast.error(t('admin_payment_error'));
    }
  };

  const openEditTotalModal = (booking) => {
    setEditingTotalBooking(booking);
    setEditTotalValue(booking?.total_price?.toString() || "");
    setShowEditTotalModal(true);
  };

  const updateBookingTotal = async () => {
    if (!editingTotalBooking) return;

    const newTotal = parseFloat(editTotalValue);
    if (!newTotal || newTotal <= 0) {
      toast.error(t('admin_total_invalid') || 'Totali duhet të jetë më i madh se 0');
      return;
    }

    try {
      const token = localStorage.getItem("adminToken");
      await axios.put(
        `${API}/admin/bookings/${editingTotalBooking.id}/total?total_price=${newTotal}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success(t('admin_total_updated') || 'Totali u përditësua');
      setShowEditTotalModal(false);
      setEditingTotalBooking(null);
      setEditTotalValue("");
      loadBookings();
      loadStats();
    } catch (error) {
      console.error("Error updating total:", error);
      toast.error(t('admin_total_update_error') || 'Gabim në përditësimin e totalit');
    }
  };

  const deleteBooking = async (bookingId) => {
    if (!window.confirm(t('admin_delete_booking_confirm') || 'A jeni i sigurt që dëshironi ta fshini këtë rezervim?')) return;

    try {
      const token = localStorage.getItem("adminToken");
      await axios.delete(`${API}/admin/bookings/${bookingId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(t('admin_booking_deleted') || 'Rezervimi u fshi');
      loadBookings();
      loadStats();
    } catch (error) {
      console.error("Error deleting booking:", error);
      toast.error(t('admin_booking_delete_error') || 'Gabim në fshirjen e rezervimit');
    }
  };

  // Car CRUD functions
  const openCarModal = (car = null) => {
    if (car) {
      setEditingCar(car);
      setCarForm({
        brand: car.brand,
        model: car.model,
        year: car.year,
        price_per_day: car.price_per_day,
        high_season_price_per_day: car.high_season_price_per_day || "",
        min_rental_days: car.min_rental_days || 1,
        tpl_price_per_day: car.tpl_price_per_day || 0,
        mini_kasko_price_per_day: car.mini_kasko_price_per_day || 0,
        full_kasko_price_per_day: car.full_kasko_price_per_day || 0,
        fuel_type: car.fuel_type,
        transmission: car.transmission,
        seats: car.seats,
        image_url: car.image_url,
        description: car.description || "",
        available: car.available
      });
      setImagePreview(car.image_url ? toAbsoluteUrl(car.image_url) : null);
      setGalleryImages(car.gallery_images || []);
      // Load existing price tiers for this car
      loadPriceTiersForCar(car.id);
    } else {
      setEditingCar(null);
      setCarForm({
        brand: "",
        model: "",
        year: new Date().getFullYear(),
        price_per_day: "",
        high_season_price_per_day: "",
        min_rental_days: 1,
        tpl_price_per_day: 0,
        mini_kasko_price_per_day: 0,
        full_kasko_price_per_day: 0,
        fuel_type: "Benzinë",
        transmission: "Automatik",
        seats: 5,
        image_url: "",
        description: "",
        available: true
      });
      setImagePreview(null);
      setEditPriceTiers([]);
      setGalleryImages([]);
    }
    setNewTier({ min_days: "", max_days: "", price_per_day: "" });
    setShowCarModal(true);
  };

  const loadPriceTiersForCar = async (carId) => {
    try {
      const token = localStorage.getItem("adminToken");
      const response = await axios.get(`${API}/admin/car-discounts?car_id=${carId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEditPriceTiers(response.data.discounts || []);
    } catch (error) {
      console.error("Error loading price tiers:", error);
      setEditPriceTiers([]);
    }
  };

  const addPriceTier = async () => {
    if (!editingCar) {
      // For new car, add to local state (will save after car creation)
      const minDays = parseInt(newTier.min_days);
      const maxDays = newTier.max_days === "" ? null : parseInt(newTier.max_days);
      const ppd = parseFloat(newTier.price_per_day);
      if (!minDays || minDays < 1 || !ppd || ppd <= 0) {
        toast.error("Plotësoni fushat saktë");
        return;
      }
      setEditPriceTiers(prev => [...prev, { id: `temp-${Date.now()}`, min_days: minDays, max_days: maxDays, price_per_day: ppd }]);
      setNewTier({ min_days: "", max_days: "", price_per_day: "" });
      return;
    }

    const minDays = parseInt(newTier.min_days);
    const maxDays = newTier.max_days === "" ? null : parseInt(newTier.max_days);
    const ppd = parseFloat(newTier.price_per_day);
    if (!minDays || minDays < 1 || !ppd || ppd <= 0) {
      toast.error("Plotësoni fushat saktë");
      return;
    }

    try {
      const token = localStorage.getItem("adminToken");
      await axios.post(`${API}/admin/car-discounts`, {
        car_id: editingCar.id,
        min_days: minDays,
        max_days: maxDays,
        price_per_day: ppd
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Periudha u shtua");
      setNewTier({ min_days: "", max_days: "", price_per_day: "" });
      loadPriceTiersForCar(editingCar.id);
    } catch (error) {
      console.error("Error adding price tier:", error);
      toast.error("Gabim në shtimin e periudhës");
    }
  };

  const removePriceTier = async (tierId) => {
    if (tierId.startsWith("temp-")) {
      setEditPriceTiers(prev => prev.filter(t => t.id !== tierId));
      return;
    }
    try {
      const token = localStorage.getItem("adminToken");
      await axios.delete(`${API}/admin/car-discounts/${tierId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Periudha u fshi");
      if (editingCar) loadPriceTiersForCar(editingCar.id);
    } catch (error) {
      console.error("Error removing price tier:", error);
      toast.error("Gabim në fshirjen e periudhës");
    }
  };

  const saveCar = async () => {
    try {
      const token = localStorage.getItem("adminToken");
      
      if (editingCar) {
        await axios.put(
          `${API}/admin/cars/${editingCar.id}`,
          carForm,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success(t('admin_car_updated'));
      } else {
        const response = await axios.post(
          `${API}/admin/cars`,
          carForm,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        // Save any temp price tiers for the new car
        const newCarId = response.data.id;
        for (const tier of editPriceTiers) {
          if (tier.id.startsWith("temp-")) {
            await axios.post(`${API}/admin/car-discounts`, {
              car_id: newCarId,
              min_days: tier.min_days,
              max_days: tier.max_days,
              price_per_day: tier.price_per_day
            }, { headers: { Authorization: `Bearer ${token}` } });
          }
        }
        toast.success(t('admin_car_added'));
      }
      
      setShowCarModal(false);
      loadCars();
      loadStats();
    } catch (error) {
      console.error("Error saving car:", error);
      toast.error(t('admin_car_save_error'));
    }
  };

  const deleteCar = async (carId) => {
    if (!window.confirm(t('admin_delete_car_confirm'))) return;
    
    try {
      const token = localStorage.getItem("adminToken");
      await axios.delete(`${API}/admin/cars/${carId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(t('admin_car_deleted'));
      loadCars();
      loadStats();
    } catch (error) {
      console.error("Error deleting car:", error);
      toast.error(t('admin_car_delete_error'));
    }
  };

  // Discount functions
  const saveDiscount = async () => {
    try {
      const token = localStorage.getItem("adminToken");
      await axios.post(
        `${API}/admin/discounts?min_days=${discountForm.min_days}&discount_percent=${discountForm.discount_percent}&description=${encodeURIComponent(discountForm.description)}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(t('admin_discount_added'));
      setShowDiscountModal(false);
      setDiscountForm({ min_days: "", discount_percent: "", description: "" });
      loadDiscounts();
    } catch (error) {
      console.error("Error saving discount:", error);
      toast.error(t('admin_discount_save_error'));
    }
  };

  const deleteDiscount = async (discountId) => {
    try {
      const token = localStorage.getItem("adminToken");
      await axios.delete(`${API}/admin/discounts/${discountId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(t('admin_discount_deleted'));
      loadDiscounts();
    } catch (error) {
      console.error("Error deleting discount:", error);
      toast.error(t('admin_discount_delete_error'));
    }
  };

  // Get car name by ID
  const getCarName = (carId) => {
    const car = cars.find(c => c.id === carId);
    return car ? `${car.brand} ${car.model}` : carId;
  };

  const toAbsoluteUrl = (url) => {
    if (!url) return "";
    if (url.startsWith("http://") || url.startsWith("https://")) return url;
    return `${BACKEND_URL}${url}`;
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowed = ["image/jpeg", "image/png", "image/webp", "image/gif"];
    if (!allowed.includes(file.type)) {
      toast.error("Formati i lejuar: JPG, PNG, WEBP, GIF");
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error("Foto max 10MB");
      return;
    }

    setImagePreview(URL.createObjectURL(file));
    setImageUploading(true);

    try {
      const token = localStorage.getItem("adminToken");
      const formData = new FormData();
      formData.append("file", file);

      const response = await axios.post(`${API}/admin/cars/upload-image`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data"
        }
      });

      setCarForm(prev => ({ ...prev, image_url: response.data.url }));
      toast.success("Foto u ngarkua me sukses!");
    } catch (error) {
      console.error("Error uploading image:", error);
      toast.error("Gabim gjatë ngarkimit të fotos");
      setImagePreview(null);
    } finally {
      setImageUploading(false);
    }
  };

  const handleGalleryUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    if (!editingCar) {
      toast.error("Ruani makinën së pari, pastaj shtoni foto galerie");
      return;
    }
    
    setGalleryUploading(true);
    const token = localStorage.getItem("adminToken");
    
    for (const file of files) {
      try {
        const formData = new FormData();
        formData.append("file", file);
        const response = await axios.post(`${API}/admin/cars/${editingCar.id}/gallery`, formData, {
          headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" }
        });
        setGalleryImages(prev => [...prev, response.data.url]);
      } catch (error) {
        console.error("Error uploading gallery image:", error);
        toast.error(`Gabim: ${file.name}`);
      }
    }
    setGalleryUploading(false);
    toast.success("Fotot u ngarkuan!");
    e.target.value = "";
  };

  const removeGalleryImage = async (url) => {
    if (!editingCar) return;
    try {
      const token = localStorage.getItem("adminToken");
      await axios.delete(`${API}/admin/cars/${editingCar.id}/gallery?url=${encodeURIComponent(url)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setGalleryImages(prev => prev.filter(img => img !== url));
      toast.success("Foto u fshi");
    } catch (error) {
      toast.error("Gabim në fshirjen e fotos");
    }
  };

  const getDocType = (url) => {
    const lower = (url || "").toLowerCase();
    if (lower.includes(".pdf")) return "pdf";
    return "image";
  };

  const openDocumentPreview = (url, label) => {
    if (!url) return;
    setSelectedDocument({ url, label, type: getDocType(url) });
    setShowDocumentModal(true);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-black">
        <div className="text-center">
          <div className="inline-block h-12 w-12 animate-spin rounded-full border-4 border-solid border-yellow-600 border-r-transparent"></div>
          <p className="mt-4 text-gray-400">{t('loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex" style={{ background: 'linear-gradient(180deg, #0a0a0a 0%, #1a1a1a 100%)' }}>
      {/* Sidebar */}
      <aside className={`${sidebarOpen ? 'w-64' : 'w-16'} bg-zinc-900 border-r border-gray-800 flex-shrink-0 transition-all duration-300 fixed h-screen z-40 overflow-y-auto`}>
        <div className="p-4 border-b border-gray-800">
          <div className={`flex items-center ${sidebarOpen ? 'justify-between' : 'justify-center'}`}>
            {sidebarOpen && (
              <div>
                <h1 className="text-lg font-bold text-white">{userRole === "employee" ? userName : "Admin Panel"}</h1>
                <p className="text-xs text-gray-500">Rent Car Kaci</p>
              </div>
            )}
            <button onClick={() => setSidebarOpen(!sidebarOpen)} className="text-gray-400 hover:text-white p-1">
              {sidebarOpen ? <ChevronLeft className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>
        <nav className="p-2 mt-2">
          <p className={`text-gray-600 text-xs uppercase tracking-wider px-3 mb-2 ${sidebarOpen ? '' : 'hidden'}`}>Menaxhimi</p>
          {[
            { id: "stats", icon: LayoutDashboard, label: "Paneli Kryesor" },
            { id: "fleet", icon: Car, label: "Flota" },
            { id: "bookings", icon: CalendarDays, label: "Rezervimet" },
            { id: "clients", icon: Users, label: "Klientët" },
            { id: "invoices", icon: Receipt, label: "Faturimi" },
            { id: "calendar", icon: Calendar, label: "Kalendari" },
            { id: "fines", icon: AlertTriangle, label: "Gjobat" },
            { id: "employees", icon: UserCheck, label: "Punonjësit", adminOnly: true },
            { id: "discounts", icon: Percent, label: "Uljet" },
            { id: "messages", icon: MessageSquare, label: "Mesazhet", badge: messages.length > 0 ? messages.length : null },
          ].filter(item => !item.adminOnly || userRole === "admin").map(({ id, icon: Icon, label, badge }) => (
            <button
              key={id}
              onClick={() => setActiveTab(id)}
              data-testid={`sidebar-${id}`}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg mb-1 transition-colors ${
                activeTab === id
                  ? "bg-yellow-600/15 text-yellow-500 border-l-2 border-yellow-500"
                  : "text-gray-400 hover:bg-zinc-800 hover:text-white"
              } ${sidebarOpen ? '' : 'justify-center'}`}
            >
              <Icon className="h-5 w-5 flex-shrink-0" />
              {sidebarOpen && <span className="text-sm">{label}</span>}
              {sidebarOpen && badge && (
                <span className="ml-auto bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">{badge}</span>
              )}
            </button>
          ))}
        </nav>
        {/* Logout at bottom */}
        <div className="absolute bottom-0 left-0 right-0 p-3 border-t border-gray-800">
          <div className="flex items-center gap-2 mb-2">
            {sidebarOpen && <LanguageSwitcher />}
          </div>
          <button
            onClick={handleLogout}
            className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-red-400 hover:bg-red-600/10 transition-colors ${sidebarOpen ? '' : 'justify-center'}`}
          >
            <LogOut className="h-5 w-5" />
            {sidebarOpen && <span className="text-sm">Çkyçu</span>}
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className={`flex-1 ${sidebarOpen ? 'ml-64' : 'ml-16'} transition-all duration-300 p-6 lg:p-8`}>

        {/* Top Bar with Notifications */}
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-xl font-bold text-white" data-testid="admin-page-title">
            {activeTab === "stats" && "Paneli Kryesor"}
            {activeTab === "fleet" && "Flota"}
            {activeTab === "bookings" && "Rezervimet"}
            {activeTab === "clients" && "Klientët"}
            {activeTab === "invoices" && "Faturimi"}
            {activeTab === "calendar" && "Kalendari"}
            {activeTab === "fines" && "Gjobat"}
            {activeTab === "employees" && "Punonjësit"}
            {activeTab === "discounts" && "Uljet"}
            {activeTab === "messages" && "Mesazhet"}
            {activeTab === "cars" && "Makinat"}
          </h1>
          <div className="flex items-center gap-3">
            <AdminNotifications />
            <span className="text-gray-500 text-sm hidden md:block">{userName}</span>
          </div>
        </div>

        {/* Stats Tab (Paneli Kryesor) */}
        {activeTab === "stats" && stats && (
          <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <Card className="bg-gradient-to-br from-zinc-900 to-black border-yellow-600/30">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="luxury-text text-xs text-gray-400 uppercase tracking-wider mb-2">{t('admin_total_bookings')}</p>
                    <p className="luxury-title text-4xl font-bold text-white">{stats.total_bookings}</p>
                  </div>
                  <Calendar className="h-12 w-12 text-yellow-600" />
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-zinc-900 to-black border-green-600/30">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="luxury-text text-xs text-gray-400 uppercase tracking-wider mb-2">{t('admin_revenue')}</p>
                    <p className="luxury-title text-4xl font-bold text-green-500">€{stats.total_revenue.toFixed(2)}</p>
                  </div>
                  <Euro className="h-12 w-12 text-green-600" />
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-zinc-900 to-black border-blue-600/30">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="luxury-text text-xs text-gray-400 uppercase tracking-wider mb-2">{t('admin_total_cars')}</p>
                    <p className="luxury-title text-4xl font-bold text-white">{stats.total_cars}</p>
                    <p className="luxury-text text-xs text-gray-500 mt-1">{stats.available_cars} {t('admin_cars_available')}</p>
                  </div>
                  <Car className="h-12 w-12 text-blue-600" />
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-zinc-900 to-black border-orange-600/30">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="luxury-text text-xs text-gray-400 uppercase tracking-wider mb-2">{t('admin_pending')}</p>
                    <p className="luxury-title text-4xl font-bold text-orange-500">{stats.pending_bookings}</p>
                  </div>
                  <Clock className="h-12 w-12 text-orange-600" />
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-zinc-900 to-black border-green-600/30">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="luxury-text text-xs text-gray-400 uppercase tracking-wider mb-2">{t('admin_confirmed')}</p>
                    <p className="luxury-title text-4xl font-bold text-green-500">{stats.confirmed_bookings}</p>
                  </div>
                  <CheckCircle className="h-12 w-12 text-green-600" />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Monthly Revenue Table */}
          <div className="mt-8">
            <h3 className="text-xl font-bold text-white mb-4">Të Ardhurat sipas Muajve</h3>
            <div className="bg-zinc-900 border border-gray-800 rounded-xl overflow-hidden">
              <table className="w-full" data-testid="monthly-revenue-table">
                <thead>
                  <tr className="border-b border-gray-800">
                    <th className="text-left text-gray-400 text-xs uppercase tracking-wider px-6 py-4">Muaji</th>
                    <th className="text-right text-gray-400 text-xs uppercase tracking-wider px-6 py-4">Rezervime</th>
                    <th className="text-right text-gray-400 text-xs uppercase tracking-wider px-6 py-4">Të paguar</th>
                    <th className="text-right text-gray-400 text-xs uppercase tracking-wider px-6 py-4">Totali</th>
                  </tr>
                </thead>
                <tbody>
                  {monthlyRevenue.length === 0 ? (
                    <tr><td colSpan="4" className="text-center text-gray-500 py-8">Nuk ka të dhëna</td></tr>
                  ) : (
                    monthlyRevenue.map((row) => {
                      const monthNames = ["Janar","Shkurt","Mars","Prill","Maj","Qershor","Korrik","Gusht","Shtator","Tetor","Nëntor","Dhjetor"];
                      const [y, m] = row.month.split("-");
                      const monthLabel = `${monthNames[parseInt(m) - 1]} ${y}`;
                      return (
                        <tr key={row.month} className="border-b border-gray-800/50 hover:bg-zinc-800/50 transition-colors">
                          <td className="px-6 py-4 text-white font-medium">{monthLabel}</td>
                          <td className="px-6 py-4 text-gray-300 text-right">{row.count}</td>
                          <td className="px-6 py-4 text-green-500 font-semibold text-right">€{row.paid.toFixed(2)}</td>
                          <td className="px-6 py-4 text-yellow-500 font-semibold text-right">€{row.total.toFixed(2)}</td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
                {monthlyRevenue.length > 0 && (
                  <tfoot>
                    <tr className="bg-zinc-800/50">
                      <td className="px-6 py-4 text-white font-bold">Totali</td>
                      <td className="px-6 py-4 text-white font-bold text-right">{monthlyRevenue.reduce((s, r) => s + r.count, 0)}</td>
                      <td className="px-6 py-4 text-green-400 font-bold text-right">€{monthlyRevenue.reduce((s, r) => s + r.paid, 0).toFixed(2)}</td>
                      <td className="px-6 py-4 text-yellow-400 font-bold text-right">€{monthlyRevenue.reduce((s, r) => s + r.total, 0).toFixed(2)}</td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          </div>

          {/* Monthly Car Reservations */}
          <div className="mt-8">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-white">Makinat e Rezervuara sipas Muajit</h3>
              <input
                type="month"
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
                className="bg-zinc-800 border border-gray-700 text-white rounded-lg px-4 py-2"
                data-testid="month-selector"
              />
            </div>
            
            {monthCars.length === 0 ? (
              <div className="bg-zinc-900 border border-gray-800 rounded-xl p-8 text-center text-gray-500">
                <Car className="h-10 w-10 mx-auto mb-2 opacity-30" />
                <p>Nuk ka makina të rezervuara për këtë muaj</p>
              </div>
            ) : (
              <div className="space-y-3">
                {monthCars.map((car) => (
                  <div key={car.car_id} className="bg-zinc-900 border border-gray-800 rounded-xl overflow-hidden" data-testid={`month-car-${car.car_id}`}>
                    <div className="p-4 flex items-center gap-4">
                      {car.car_image && (
                        <div className="w-20 h-14 rounded-lg bg-zinc-800 overflow-hidden flex-shrink-0">
                          <img src={car.car_image?.startsWith("http") ? car.car_image : `${BACKEND_URL}${car.car_image}`} alt="" className="w-full h-full object-contain" />
                        </div>
                      )}
                      <div className="flex-1">
                        <p className="text-white font-semibold text-lg">{car.car_name}</p>
                        <div className="flex gap-6 mt-1">
                          <span className="text-gray-400 text-sm">{car.bookings_count} rezervime</span>
                          <span className="text-blue-400 text-sm">{car.total_days} ditë</span>
                          <span className="text-yellow-500 text-sm font-medium">€{car.total_revenue?.toFixed(2)}</span>
                        </div>
                      </div>
                    </div>
                    {/* Booking details */}
                    <div className="border-t border-gray-800/50 px-4 py-2 bg-zinc-950/50">
                      {car.bookings.map((b, idx) => {
                        const sc = { pending: "text-yellow-500", confirmed: "text-blue-500", active: "text-green-500", completed: "text-gray-400" };
                        const sl = { pending: "Në pritje", confirmed: "Konfirmuar", active: "Aktive", completed: "Përfunduar" };
                        return (
                          <div key={idx} className="flex items-center justify-between py-1.5 text-sm">
                            <span className="text-gray-300">{b.customer_name}</span>
                            <span className="text-gray-500">{b.pickup_date} → {b.return_date}</span>
                            <span className={sc[b.status] || "text-gray-400"}>{sl[b.status] || b.status}</span>
                            <span className="text-yellow-600 font-medium">€{b.total_price?.toFixed(2)}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          </>
        )}

        {/* Fleet Tab (Flota) */}
        {activeTab === "fleet" && (
          <AdminFlota cars={cars} onAddCar={() => openCarModal()} onEditCar={(car) => openCarModal(car)} onDeleteCar={(carId) => deleteCar(carId)} onRefresh={loadCars} />
        )}

        {/* Reservations Tab (Rezervimet) - Enhanced with stat cards + existing management */}
        {activeTab === "bookings" && (
          <div>
            <AdminRezervimet bookings={bookings} cars={cars} onRefresh={loadBookings} />
            {/* Detailed Booking Management below */}
            <div className="mt-8 space-y-4">
              <h3 className="text-lg font-semibold text-white border-t border-gray-800 pt-6">Menaxhimi i Rezervimeve</h3>
            {bookings.length === 0 ? (
              <p className="text-gray-400 text-center py-8">{t('admin_no_bookings')}</p>
            ) : (
              bookings.map((booking) => (
                <Card key={booking.id} className="bg-zinc-900 border-yellow-600/20">
                  <CardContent className="p-6">
                    <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                      <div>
                        <p className="luxury-text text-xs text-gray-500 uppercase mb-1">{t('admin_car')}</p>
                        <p className="text-yellow-600 font-semibold">{getCarName(booking.car_id)}</p>
                      </div>
                      <div>
                        <p className="luxury-text text-xs text-gray-500 uppercase mb-1">{t('admin_client')}</p>
                        <p className="text-white font-semibold">{booking.customer_name}</p>
                        <p className="text-gray-400 text-sm">{booking.customer_phone}</p>
                      </div>
                      <div>
                        <p className="luxury-text text-xs text-gray-500 uppercase mb-1">{t('admin_dates')}</p>
                        <p className="text-white text-sm">
                          {new Date(booking.pickup_date).toLocaleDateString('sq-AL')}
                          {booking.pickup_time && <span className="text-purple-400 ml-1">({booking.pickup_time})</span>}
                        </p>
                        <p className="text-white text-sm">
                          {new Date(booking.return_date).toLocaleDateString('sq-AL')}
                          {booking.return_time && <span className="text-purple-400 ml-1">({booking.return_time})</span>}
                        </p>
                        {booking.is_same_day && (
                          <span className="inline-block mt-1 px-2 py-0.5 rounded text-xs bg-purple-900/30 text-purple-400">
                            🕐 {t('same_day_booking') || 'Same-day'}
                          </span>
                        )}
                      </div>
                      <div>
                        <p className="luxury-text text-xs text-gray-500 uppercase mb-1">{t('admin_price_payment')}</p>
                        {booking.discount_percent > 0 ? (
                          <>
                            <p className="text-gray-500 text-sm line-through">€{booking.base_price?.toFixed(2) || booking.total_price}</p>
                            <p className="text-yellow-600 text-2xl font-bold">€{booking.total_price}</p>
                            <span className="inline-block px-2 py-0.5 rounded text-xs bg-green-900/30 text-green-400 mb-1">
                              -{booking.discount_percent}% {t('admin_discount_applied')}
                            </span>
                          </>
                        ) : (
                          <p className="text-yellow-600 text-2xl font-bold">€{booking.total_price}</p>
                        )}
                        {/* Payment Status */}
                        <div className="mt-1 space-y-1">
                          <span className={`inline-block px-2 py-1 rounded text-xs uppercase ${
                            booking.payment_status === 'paid' 
                              ? 'bg-green-900/50 text-green-300 border border-green-500/50'
                              : booking.payment_status === 'deposit'
                              ? 'bg-blue-900/50 text-blue-300 border border-blue-500/50'
                              : 'bg-gray-800 text-gray-400 border border-gray-600'
                          }`}>
                            {booking.payment_status === 'paid' ? `✓ ${t('admin_paid')}` : 
                             booking.payment_status === 'deposit' ? `${t('admin_deposit')} €${booking.deposit_amount || 0}` : 
                             t('admin_unpaid')}
                          </span>
                          {booking.payment_status === 'deposit' && (
                            <p className="text-xs text-gray-400">
                              {t('admin_remaining')}: €{Math.max(0, (booking.total_price - (booking.amount_paid || 0))).toFixed(2)}
                            </p>
                          )}
                        </div>
                      </div>
                      <div>
                        <p className="luxury-text text-xs text-gray-500 uppercase mb-1">{t('admin_status')}</p>
                        <span className={`inline-block px-2 py-1 rounded text-xs uppercase ${
                          booking.status === 'confirmed' 
                            ? 'bg-green-900/30 text-green-400 border border-green-600/30'
                            : booking.status === 'cancelled'
                            ? 'bg-red-900/30 text-red-400 border border-red-600/30'
                            : 'bg-orange-900/30 text-orange-400 border border-orange-600/30'
                        }`}>
                          {booking.status === 'confirmed' ? t('admin_status_confirmed') : booking.status === 'cancelled' ? t('admin_status_cancelled') : t('admin_status_pending')}
                        </span>
                      </div>

                      {/* Documents */}
                      <div>
                        <p className="luxury-text text-xs text-gray-500 uppercase mb-2">{t('documents_required') || 'Dokumentet'}</p>
                        <div className="space-y-2">
                          <div className="flex items-center justify-between gap-2 bg-zinc-800/60 border border-gray-700 rounded-md px-3 py-2">
                            <div className="flex items-center gap-2 min-w-0">
                              <FileText className="h-4 w-4 text-yellow-600" />
                              <span className="text-gray-200 text-sm truncate">ID</span>
                            </div>
                            <div className="flex items-center gap-2">
                              {booking.id_document_url ? (
                                <>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="border-yellow-600/40 text-yellow-600 hover:bg-yellow-600/10"
                                    onClick={() => openDocumentPreview(booking.id_document_url, 'ID')}
                                  >
                                    {t('view_photo') || 'Shiko'}
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    className="text-gray-300 hover:text-white"
                                    onClick={() => window.open(toAbsoluteUrl(booking.id_document_url), '_blank')}
                                  >
                                    <ExternalLink className="h-4 w-4" />
                                  </Button>
                                </>
                              ) : (
                                <span className="text-xs text-gray-500">—</span>
                              )}
                            </div>
                          </div>

                          <div className="flex items-center justify-between gap-2 bg-zinc-800/60 border border-gray-700 rounded-md px-3 py-2">
                            <div className="flex items-center gap-2 min-w-0">
                              <FileText className="h-4 w-4 text-yellow-600" />
                              <span className="text-gray-200 text-sm truncate">{t('upload_license') || 'Patentë'}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              {booking.license_document_url ? (
                                <>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="border-yellow-600/40 text-yellow-600 hover:bg-yellow-600/10"
                                    onClick={() => openDocumentPreview(booking.license_document_url, 'Patentë')}
                                  >
                                    {t('view_photo') || 'Shiko'}
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    className="text-gray-300 hover:text-white"
                                    onClick={() => window.open(toAbsoluteUrl(booking.license_document_url), '_blank')}
                                  >
                                    <ExternalLink className="h-4 w-4" />
                                  </Button>
                                </>
                              ) : (
                                <span className="text-xs text-gray-500">—</span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="flex flex-col space-y-2">
                        {/* Edit Total (only pending) */}
                        {booking.status === 'pending' && (
                          <Button
                            onClick={() => openEditTotalModal(booking)}
                            size="sm"
                            variant="outline"
                            className="border-yellow-600/40 text-yellow-600 hover:bg-yellow-600/10"
                          >
                            <Pencil className="h-4 w-4 mr-1" />
                            {t('admin_edit_total') || 'Ndrysho Totalin'}
                          </Button>
                        )}

                        {/* Delete Booking */}
                        <Button
                          onClick={() => deleteBooking(booking.id)}
                          size="sm"
                          variant="outline"
                          className="border-red-600/40 text-red-600 hover:bg-red-600/10"
                        >
                          <Trash className="h-4 w-4 mr-1" />
                          {t('admin_delete_booking') || 'Fshi Rezervimin'}
                        </Button>
                        {booking.status === 'pending' && (
                          <Button
                            onClick={() => updateBookingStatus(booking.id, 'confirmed')}
                            size="sm"
                            className="bg-green-700 hover:bg-green-600"
                          >
                            <CheckCircle className="h-4 w-4 mr-1" />
                            {t('admin_confirm')}
                          </Button>
                        )}
                        {booking.status !== 'cancelled' && (
                          <>
                            <Button
                              onClick={() => openPaymentModal(booking)}
                              size="sm"
                              className="bg-blue-700 hover:bg-blue-600"
                            >
                              <CreditCard className="h-4 w-4 mr-1" />
                              {t('admin_payment')}
                            </Button>
                            <Button
                              onClick={() => updateBookingStatus(booking.id, 'cancelled')}
                              size="sm"
                              variant="outline"
                              className="border-red-600/40 text-red-600 hover:bg-red-600/10"
                            >
                              <X className="h-4 w-4 mr-1" />
                              {t('admin_cancel')}
                            </Button>
                          </>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
            </div>
          </div>
        )}

        {/* Clients Tab (Klientët) */}
        {activeTab === "clients" && <AdminKlientet />}

        {/* Invoices Tab (Faturimi) */}
        {activeTab === "invoices" && <AdminFaturimi cars={cars} />}

        {/* Calendar Tab (Kalendari) */}
        {activeTab === "calendar" && <AdminKalendari />}

        {/* Fines Tab (Gjobat) */}
        {activeTab === "fines" && <AdminGjobat cars={cars} clients={clients} />}

        {/* Employees Tab (Punonjësit) - Admin only */}
        {activeTab === "employees" && userRole === "admin" && <AdminPunonjesit />}

        {/* Cars Tab */}
        {activeTab === "cars" && (
          <div>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold text-white">{t('admin_car_management')}</h2>
              <Button onClick={() => openCarModal()} className="bg-yellow-600 hover:bg-yellow-500">
                <Plus className="h-4 w-4 mr-2" />
                {t('admin_add_car')}
              </Button>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {cars.map((car) => (
                <Card key={car.id} className={`bg-zinc-900 border-2 ${car.is_currently_booked ? 'border-orange-500/50' : 'border-green-500/30'}`}>
                  <CardContent className="p-4">
                    <img 
                      src={car.image_url} 
                      alt={`${car.brand} ${car.model}`}
                      className="w-full h-40 object-cover rounded-lg mb-4"
                    />
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <h3 className="text-white font-bold">{car.brand} {car.model}</h3>
                        <p className="text-gray-400 text-sm">{car.year}</p>
                      </div>
                      <span className={`px-2 py-1 rounded text-xs ${
                        car.is_currently_booked 
                          ? 'bg-orange-900/30 text-orange-400' 
                          : 'bg-green-900/30 text-green-400'
                      }`}>
                        {car.is_currently_booked ? t('admin_car_reserved') : t('admin_car_free')}
                      </span>
                    </div>
                    
                    <p className="text-yellow-600 text-xl font-bold mb-1">€{car.price_per_day}{t('per_day')}</p>
                    {car.price_tiers && car.price_tiers.length > 0 && (
                      <div className="mb-2">
                        {car.price_tiers.map((tier, idx) => (
                          <p key={idx} className="text-gray-400 text-xs">
                            {tier.min_days}-{tier.max_days ? tier.max_days : '+'} {t('days') || 'ditë'}: <span className="text-yellow-500">€{tier.price_per_day}/{t('per_day') || 'ditë'}</span>
                          </p>
                        ))}
                      </div>
                    )}
                    
                    <div className="text-gray-400 text-sm mb-4">
                      <p>{car.fuel_type} • {car.transmission} • {car.seats} {t('seats')}</p>
                    </div>
                    
                    {car.active_bookings && car.active_bookings.length > 0 && (
                      <div className="bg-orange-900/20 rounded p-2 mb-4">
                        <p className="text-orange-400 text-xs uppercase mb-1">{t('admin_active_bookings')}:</p>
                        {car.active_bookings.map((b, idx) => (
                          <p key={idx} className="text-gray-300 text-xs">
                            {new Date(b.pickup_date).toLocaleDateString('sq-AL')} - {new Date(b.return_date).toLocaleDateString('sq-AL')}
                            <span className={`ml-2 ${b.status === 'confirmed' ? 'text-green-400' : 'text-orange-400'}`}>
                              ({b.status === 'confirmed' ? t('confirmed') : t('pending')})
                            </span>
                          </p>
                        ))}
                      </div>
                    )}
                    
                    <div className="flex space-x-2">
                      <Button 
                        onClick={() => openCarModal(car)} 
                        size="sm" 
                        variant="outline"
                        className="flex-1 border-yellow-600/40 text-yellow-600"
                      >
                        <Edit className="h-4 w-4 mr-1" />
                        {t('admin_edit')}
                      </Button>
                      <Button 
                        onClick={() => deleteCar(car.id)} 
                        size="sm" 
                        variant="outline"
                        className="border-red-600/40 text-red-600"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Discounts Tab */}
        {activeTab === "discounts" && (
          <div>
            <div className="flex justify-between items-center mb-6">
              <div>
                <h2 className="text-xl font-bold text-white">{t('admin_discounts_title')}</h2>
                <p className="text-gray-400 text-sm">{t('admin_discounts_subtitle')}</p>
              </div>
              <Button onClick={() => setShowDiscountModal(true)} className="bg-yellow-600 hover:bg-yellow-500">
                <Plus className="h-4 w-4 mr-2" />
                {t('admin_add_discount')}
              </Button>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {discounts.map((discount, idx) => (
                <Card key={discount.id || idx} className="bg-zinc-900 border-green-600/30">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between mb-4">
                      <Percent className="h-10 w-10 text-green-500" />
                      <Button 
                        onClick={() => deleteDiscount(discount.id)} 
                        size="sm" 
                        variant="ghost"
                        className="text-red-500 hover:text-red-400"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                    <p className="text-4xl font-bold text-green-500 mb-2">-{discount.discount_percent}%</p>
                    <p className="text-white font-semibold">{discount.min_days}{t('admin_days_plus')}</p>
                    <p className="text-gray-400 text-sm">{discount.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Messages Tab */}
        {activeTab === "messages" && (
          <div>
            <div className="flex justify-between items-center mb-6">
              <div>
                <h2 className="text-xl font-bold text-white">{t('admin_messages_title')}</h2>
                <p className="text-gray-400 text-sm">{t('admin_messages_subtitle')}</p>
              </div>
              <div className="flex items-center gap-2 text-gray-400">
                <Mail className="h-5 w-5" />
                <span>{messages.length} {t('admin_messages_count')}</span>
              </div>
            </div>
            
            {messages.length === 0 ? (
              <div className="text-center py-12 bg-zinc-900 rounded-lg border border-gray-800">
                <MessageSquare className="h-12 w-12 text-gray-600 mx-auto mb-4" />
                <p className="text-gray-400">{t('admin_no_messages')}</p>
                <p className="text-gray-500 text-sm">{t('admin_messages_appear')}</p>
              </div>
            ) : (
              <div className="space-y-4">
                {messages.map((msg) => (
                  <Card key={msg.id} className="bg-zinc-900 border-gray-800">
                    <CardContent className="p-6">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="flex items-center gap-4 mb-3">
                            <div>
                              <p className="text-white font-semibold text-lg">{msg.name}</p>
                              <div className="flex items-center gap-3 text-gray-400 text-sm">
                                <span>{msg.email}</span>
                                <span>•</span>
                                <span>{msg.phone}</span>
                              </div>
                            </div>
                          </div>
                          <div className="bg-zinc-800 rounded-lg p-4 mt-3">
                            <p className="text-gray-300 whitespace-pre-wrap">{msg.message}</p>
                          </div>
                          <p className="text-gray-500 text-xs mt-3">
                            {new Date(msg.created_at).toLocaleString('sq-AL', {
                              day: 'numeric',
                              month: 'long',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </p>
                        </div>
                        <div className="flex gap-2 ml-4">
                          <Button
                            onClick={() => window.open(`mailto:${msg.email}`, '_blank')}
                            size="sm"
                            className="bg-blue-600 hover:bg-blue-500"
                          >
                            <Mail className="h-4 w-4 mr-1" />
                            {t('admin_reply')}
                          </Button>
                          <Button
                            onClick={() => deleteMessage(msg.id)}
                            size="sm"
                            variant="outline"
                            className="border-red-600/40 text-red-500 hover:bg-red-600/10"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}

      {/* Car Modal */}
      {showCarModal && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4">
          <div className="bg-zinc-900 rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-bold text-white">
                  {editingCar ? t('admin_edit_car') : t('admin_add_new_car')}
                </h2>
                <button onClick={() => setShowCarModal(false)} className="text-gray-400 hover:text-white">
                  <X className="h-6 w-6" />
                </button>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-300">{t('admin_car_brand')}</Label>
                  <Input 
                    value={carForm.brand}
                    onChange={(e) => setCarForm({...carForm, brand: e.target.value})}
                    className="bg-zinc-800 border-gray-700 text-white mt-1"
                    placeholder="Mercedes-Benz"
                  />
                </div>
                <div>
                  <Label className="text-gray-300">{t('admin_car_model')}</Label>
                  <Input 
                    value={carForm.model}
                    onChange={(e) => setCarForm({...carForm, model: e.target.value})}
                    className="bg-zinc-800 border-gray-700 text-white mt-1"
                    placeholder="G-Class"
                  />
                </div>
                <div>
                  <Label className="text-gray-300">{t('admin_car_year')}</Label>
                  <Input 
                    type="number"
                    value={carForm.year}
                    onChange={(e) => setCarForm({...carForm, year: parseInt(e.target.value)})}
                    className="bg-zinc-800 border-gray-700 text-white mt-1"
                  />
                </div>
                <div>
                  <Label className="text-gray-300">{t('admin_car_price')}</Label>
                  <Input 
                    type="number"
                    value={carForm.price_per_day}
                    onChange={(e) => setCarForm({...carForm, price_per_day: parseFloat(e.target.value)})}
                    className="bg-zinc-800 border-gray-700 text-white mt-1"
                    placeholder="100"
                  />
                </div>

                <div>
                  <Label className="text-gray-300">{t('admin_car_price_high_season') || 'Çmimi/ditë (Fluks)'}</Label>
                  <Input 
                    type="number"
                    value={carForm.high_season_price_per_day}
                    onChange={(e) => setCarForm({...carForm, high_season_price_per_day: e.target.value === '' ? '' : parseFloat(e.target.value)})}
                    className="bg-zinc-800 border-gray-700 text-white mt-1"
                    placeholder="120"
                  />
                  <p className="text-xs text-gray-500 mt-1">{t('admin_high_season_note') || '1 Qershor - 30 Shtator'}</p>
                </div>

                <div>
                  <Label className="text-gray-300">{t('admin_car_min_days') || 'Minimum ditë rezervimi'}</Label>
                  <Input 
                    type="number"
                    value={carForm.min_rental_days}
                    onChange={(e) => setCarForm({...carForm, min_rental_days: parseInt(e.target.value || '1')})}
                    className="bg-zinc-800 border-gray-700 text-white mt-1"
                    placeholder="1"
                  />
                </div>

                <div className="col-span-2">
                  <p className="luxury-text text-sm text-gray-400 uppercase tracking-wider mb-2">{t('admin_insurance_prices') || 'Siguracionet (€/ditë)'}</p>
                </div>
                <div>
                  <Label className="text-gray-300">{t('insurance_tpl') || 'TPL'}</Label>
                  <Input
                    type="number"
                    value={carForm.tpl_price_per_day}
                    onChange={(e) => setCarForm({ ...carForm, tpl_price_per_day: parseFloat(e.target.value || '0') })}
                    className="bg-zinc-800 border-gray-700 text-white mt-1"
                    placeholder="0"
                  />
                </div>
                <div>
                  <Label className="text-gray-300">{t('insurance_mini_kasko') || 'Mini Kasko'}</Label>
                  <Input
                    type="number"
                    value={carForm.mini_kasko_price_per_day}
                    onChange={(e) => setCarForm({ ...carForm, mini_kasko_price_per_day: parseFloat(e.target.value || '0') })}
                    className="bg-zinc-800 border-gray-700 text-white mt-1"
                    placeholder="0"
                  />
                </div>
                <div>
                  <Label className="text-gray-300">{t('insurance_full_kasko') || 'Full Kasko'}</Label>
                  <Input
                    type="number"
                    value={carForm.full_kasko_price_per_day}
                    onChange={(e) => setCarForm({ ...carForm, full_kasko_price_per_day: parseFloat(e.target.value || '0') })}
                    className="bg-zinc-800 border-gray-700 text-white mt-1"
                    placeholder="0"
                  />
                </div>

                <div>
                  <Label className="text-gray-300">{t('admin_car_fuel')}</Label>
                  <select 
                    value={carForm.fuel_type}
                    onChange={(e) => setCarForm({...carForm, fuel_type: e.target.value})}
                    className="w-full bg-zinc-800 border border-gray-700 text-white rounded-md px-3 py-2 mt-1"
                  >
                    <option value="Benzinë">{t('filter_fuel_petrol')}</option>
                    <option value="Naftë">{t('filter_fuel_diesel')}</option>
                    <option value="Elektrike">{t('filter_fuel_electric')}</option>
                    <option value="Hibride">{t('filter_fuel_hybrid')}</option>
                  </select>
                </div>
                <div>
                  <Label className="text-gray-300">{t('admin_car_transmission')}</Label>
                  <select 
                    value={carForm.transmission}
                    onChange={(e) => setCarForm({...carForm, transmission: e.target.value})}
                    className="w-full bg-zinc-800 border border-gray-700 text-white rounded-md px-3 py-2 mt-1"
                  >
                    <option value="Automatik">{t('filter_trans_auto')}</option>
                    <option value="Manual">{t('filter_trans_manual')}</option>
                  </select>
                </div>
                <div>
                  <Label className="text-gray-300">{t('admin_car_seats')}</Label>
                  <Input 
                    type="number"
                    value={carForm.seats}
                    onChange={(e) => setCarForm({...carForm, seats: parseInt(e.target.value)})}
                    className="bg-zinc-800 border-gray-700 text-white mt-1"
                  />
                </div>
                <div>
                  <Label className="text-gray-300">{t('admin_car_available')}</Label>
                  <select 
                    value={carForm.available ? "true" : "false"}
                    onChange={(e) => setCarForm({...carForm, available: e.target.value === "true"})}
                    className="w-full bg-zinc-800 border border-gray-700 text-white rounded-md px-3 py-2 mt-1"
                  >
                    <option value="true">{t('admin_yes')}</option>
                    <option value="false">{t('admin_no')}</option>
                  </select>
                </div>
                <div className="col-span-2">
                  <Label className="text-gray-300">{t('admin_car_image')}</Label>
                  <div className="mt-1 space-y-3">
                    {/* Image Preview */}
                    {(imagePreview || carForm.image_url) && (
                      <div className="relative w-full h-48 bg-zinc-800 rounded-lg overflow-hidden border border-gray-700">
                        <img
                          src={imagePreview || toAbsoluteUrl(carForm.image_url)}
                          alt="Preview"
                          className="w-full h-full object-contain"
                          data-testid="car-image-preview"
                        />
                      </div>
                    )}
                    {/* Upload Button */}
                    <label
                      data-testid="car-image-upload-btn"
                      className={`flex items-center justify-center gap-2 w-full px-4 py-3 rounded-lg border-2 border-dashed cursor-pointer transition-colors ${
                        imageUploading
                          ? "border-yellow-600/50 bg-yellow-600/10 text-yellow-600"
                          : "border-gray-600 hover:border-yellow-600/60 text-gray-400 hover:text-yellow-600"
                      }`}
                    >
                      {imageUploading ? (
                        <>
                          <div className="h-5 w-5 animate-spin rounded-full border-2 border-yellow-600 border-r-transparent" />
                          <span>Po ngarkohet...</span>
                        </>
                      ) : (
                        <>
                          <Upload className="h-5 w-5" />
                          <span>{carForm.image_url ? "Ndrysho foton" : "Ngarko foton"}</span>
                        </>
                      )}
                      <input
                        type="file"
                        accept="image/jpeg,image/png,image/webp,image/gif"
                        onChange={handleImageUpload}
                        className="hidden"
                        disabled={imageUploading}
                        data-testid="car-image-file-input"
                      />
                    </label>
                  </div>
                </div>
                <div className="col-span-2">
                  <Label className="text-gray-300">{t('admin_car_description')}</Label>
                  <Textarea 
                    value={carForm.description}
                    onChange={(e) => setCarForm({...carForm, description: e.target.value})}
                    className="bg-zinc-800 border-gray-700 text-white mt-1"
                    rows={3}
                  />
                </div>

                {/* Gallery Images */}
                {editingCar && (
                <div className="col-span-2 border-t border-gray-700 pt-4 mt-2">
                  <div className="flex justify-between items-center mb-3">
                    <Label className="text-gray-300">Galeria e Fotove</Label>
                    <label className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm cursor-pointer transition-colors ${galleryUploading ? "bg-yellow-600/10 text-yellow-600" : "bg-zinc-800 hover:bg-zinc-700 text-gray-300"}`}>
                      {galleryUploading ? (
                        <><div className="h-4 w-4 animate-spin rounded-full border-2 border-yellow-600 border-r-transparent" /><span>Po ngarkohet...</span></>
                      ) : (
                        <><Upload className="h-4 w-4" /><span>Shto foto</span></>
                      )}
                      <input type="file" accept="image/*" multiple onChange={handleGalleryUpload} className="hidden" disabled={galleryUploading} data-testid="gallery-upload-input" />
                    </label>
                  </div>
                  {galleryImages.length > 0 ? (
                    <div className="grid grid-cols-4 gap-2">
                      {galleryImages.map((img, idx) => (
                        <div key={idx} className="relative group h-24 bg-zinc-800 rounded-lg overflow-hidden border border-gray-700">
                          <img src={toAbsoluteUrl(img)} alt="" className="w-full h-full object-contain" />
                          <button onClick={() => removeGalleryImage(img)} className="absolute top-1 right-1 bg-red-600 rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                            <X className="h-3 w-3 text-white" />
                          </button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-500 text-sm text-center py-4">Nuk ka foto galerie. Shtoni foto me butonin sipër.</p>
                  )}
                </div>
                )}
              </div>

              {/* ===== Price Tiers Section ===== */}
              <div className="mt-6 border-t border-gray-700 pt-6">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-white font-semibold text-lg">Çmimet sipas Periudhave</h3>
                  <Button
                    size="sm"
                    onClick={addPriceTier}
                    className="bg-yellow-600 hover:bg-yellow-500"
                    data-testid="add-price-tier-btn"
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    Shto Periudhë
                  </Button>
                </div>

                {/* New Tier Form */}
                <div className="grid grid-cols-3 gap-3 mb-4">
                  <div>
                    <Label className="text-gray-400 text-xs">Nga (ditë)</Label>
                    <Input
                      type="number"
                      value={newTier.min_days}
                      onChange={(e) => setNewTier({ ...newTier, min_days: e.target.value })}
                      className="bg-zinc-800 border-gray-700 text-white mt-1"
                      placeholder="1"
                      data-testid="tier-min-days"
                    />
                  </div>
                  <div>
                    <Label className="text-gray-400 text-xs">Deri (ditë)</Label>
                    <Input
                      type="number"
                      value={newTier.max_days}
                      onChange={(e) => setNewTier({ ...newTier, max_days: e.target.value })}
                      className="bg-zinc-800 border-gray-700 text-white mt-1"
                      placeholder="∞"
                      data-testid="tier-max-days"
                    />
                  </div>
                  <div>
                    <Label className="text-gray-400 text-xs">€/ditë</Label>
                    <Input
                      type="number"
                      value={newTier.price_per_day}
                      onChange={(e) => setNewTier({ ...newTier, price_per_day: e.target.value })}
                      className="bg-zinc-800 border-gray-700 text-white mt-1"
                      placeholder="30"
                      data-testid="tier-price-per-day"
                    />
                  </div>
                </div>

                {/* Existing Tiers */}
                {editPriceTiers.length > 0 ? (
                  <div className="space-y-2">
                    {editPriceTiers.map((tier) => (
                      <div key={tier.id} className="flex items-center justify-between bg-zinc-800 border border-gray-700 rounded-md px-4 py-3">
                        <div className="text-gray-200 text-sm">
                          <span className="font-semibold">{tier.min_days}</span>
                          {tier.max_days ? (
                            <span> - <span className="font-semibold">{tier.max_days}</span> ditë</span>
                          ) : (
                            <span>+ ditë</span>
                          )}
                          <span className="text-gray-400 mx-2">→</span>
                          <span className="text-yellow-500 font-semibold">€{tier.price_per_day}/ditë</span>
                        </div>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-red-500 hover:text-red-400"
                          onClick={() => removePriceTier(tier.id)}
                          data-testid={`remove-tier-${tier.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-sm text-center py-3">Nuk ka periudha çmimesh. Shtoni një periudhë me butonin sipër.</p>
                )}
              </div>
              
              <div className="flex justify-end space-x-3 mt-6">
                <Button variant="outline" onClick={() => setShowCarModal(false)} className="border-gray-600 text-gray-300">
                  {t('admin_cancel_action')}
                </Button>
                <Button onClick={saveCar} className="bg-yellow-600 hover:bg-yellow-500">
                  <Save className="h-4 w-4 mr-2" />
                  Ruaj Ndryshimet
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Discount Modal */}
      {showDiscountModal && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4">
          <div className="bg-zinc-900 rounded-lg max-w-md w-full">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-bold text-white">{t('admin_add_new_discount')}</h2>
                <button onClick={() => setShowDiscountModal(false)} className="text-gray-400 hover:text-white">
                  <X className="h-6 w-6" />
                </button>
              </div>
              
              <div className="space-y-4">
                <div>
                  <Label className="text-gray-300">{t('admin_min_days')}</Label>
                  <Input 
                    type="number"
                    value={discountForm.min_days}
                    onChange={(e) => setDiscountForm({...discountForm, min_days: e.target.value})}
                    className="bg-zinc-800 border-gray-700 text-white mt-1"
                    placeholder="7"
                  />
                </div>
                <div>
                  <Label className="text-gray-300">{t('admin_discount_percent')}</Label>
                  <Input 
                    type="number"
                    value={discountForm.discount_percent}
                    onChange={(e) => setDiscountForm({...discountForm, discount_percent: e.target.value})}
                    className="bg-zinc-800 border-gray-700 text-white mt-1"
                    placeholder="10"
                  />
                </div>
                <div>
                  <Label className="text-gray-300">{t('admin_discount_description')}</Label>
                  <Input 
                    value={discountForm.description}
                    onChange={(e) => setDiscountForm({...discountForm, description: e.target.value})}
                    className="bg-zinc-800 border-gray-700 text-white mt-1"
                    placeholder="7+ ditë: -10%"
                  />
                </div>
              </div>
              
              <div className="flex justify-end space-x-3 mt-6">
                <Button variant="outline" onClick={() => setShowDiscountModal(false)} className="border-gray-600 text-gray-300">
                  {t('admin_cancel_action')}
                </Button>
                <Button onClick={saveDiscount} className="bg-yellow-600 hover:bg-yellow-500">
                  <Save className="h-4 w-4 mr-2" />
                  {t('admin_save')}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Document Preview Modal */}
      {showDocumentModal && selectedDocument && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4">
          <div className="bg-zinc-900 rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-bold text-white">
                  {t('documents_required') || 'Dokumentet'}: {selectedDocument.label}
                </h2>
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    className="border-yellow-600/40 text-yellow-600 hover:bg-yellow-600/10"
                    onClick={() => window.open(toAbsoluteUrl(selectedDocument.url), '_blank')}
                  >
                    <ExternalLink className="h-4 w-4 mr-2" />
                    {t('admin_view_document') || 'Hap në tab të ri'}
                  </Button>
                  <button
                    onClick={() => {
                      setShowDocumentModal(false);
                      setSelectedDocument(null);
                    }}
                    className="text-gray-400 hover:text-white"
                  >
                    <X className="h-6 w-6" />
                  </button>
                </div>
              </div>

              <div className="bg-zinc-800 rounded-lg border border-gray-700 overflow-hidden">
                {selectedDocument.type === 'pdf' ? (
                  <iframe
                    title="document-preview"
                    src={toAbsoluteUrl(selectedDocument.url)}
                    className="w-full"
                    style={{ height: '70vh' }}
                  />
                ) : (
                  <div className="bg-zinc-500 p-3">
                    <img
                      src={toAbsoluteUrl(selectedDocument.url)}
                      alt={selectedDocument.label}
                      className="w-full max-h-[70vh] object-contain"
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Total Modal */}
      {showEditTotalModal && editingTotalBooking && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4">
          <div className="bg-zinc-900 rounded-lg max-w-md w-full">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-bold text-white">{t('admin_edit_total') || 'Ndrysho Totalin'}</h2>
                <button onClick={() => {
                  setShowEditTotalModal(false);
                  setEditingTotalBooking(null);
                  setEditTotalValue("");
                }} className="text-gray-400 hover:text-white">
                  <X className="h-6 w-6" />
                </button>
              </div>

              <div className="space-y-4">
                <div className="bg-zinc-800 rounded-lg p-4 border border-gray-700">
                  <p className="text-gray-400 text-sm">{t('admin_car')}</p>
                  <p className="text-white font-semibold">{getCarName(editingTotalBooking.car_id)}</p>
                  <p className="text-gray-500 text-xs mt-2">Booking ID: {editingTotalBooking.id}</p>
                </div>

                <div>
                  <Label className="text-gray-300">{t('admin_total_label') || 'Totali'}</Label>
                  <Input
                    type="number"
                    value={editTotalValue}
                    onChange={(e) => setEditTotalValue(e.target.value)}
                    className="bg-zinc-800 border-gray-700 text-white mt-2"
                    placeholder="0"
                  />
                  <p className="text-xs text-gray-500 mt-2">
                    {t('admin_total_pending_note') || 'Lejohet vetëm kur rezervimi është në pritje (pending).'}
                  </p>
                </div>
              </div>

              <div className="flex justify-end space-x-3 mt-6">
                <Button variant="outline" onClick={() => {
                  setShowEditTotalModal(false);
                  setEditingTotalBooking(null);
                  setEditTotalValue("");
                }} className="border-gray-600 text-gray-300">
                  {t('admin_cancel_action')}
                </Button>
                <Button onClick={updateBookingTotal} className="bg-yellow-600 hover:bg-yellow-500">
                  <Save className="h-4 w-4 mr-2" />
                  {t('admin_save')}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Payment Modal */}
      {showPaymentModal && selectedBooking && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4">
          <div className="bg-zinc-900 rounded-lg max-w-md w-full">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-bold text-white">{t('admin_manage_payment')}</h2>
                <button onClick={() => {
                  setShowPaymentModal(false);
                  setSelectedBooking(null);
                  setDepositAmount("");
                }} className="text-gray-400 hover:text-white">
                  <X className="h-6 w-6" />
                </button>
              </div>
              
              {/* Booking Info */}
              <div className="bg-zinc-800 rounded-lg p-4 mb-6">
                <p className="text-gray-400 text-sm">{t('admin_client_label')}</p>
                <p className="text-white font-semibold">{selectedBooking.customer_name}</p>
                <div className="flex justify-between mt-3">
                  <div>
                    <p className="text-gray-400 text-xs">{t('admin_total_label')}</p>
                    <p className="text-yellow-500 text-xl font-bold">€{selectedBooking.total_price}</p>
                  </div>
                  <div>
                    <p className="text-gray-400 text-xs">{t('admin_paid_so_far')}</p>
                    <p className="text-green-500 text-xl font-bold">€{selectedBooking.amount_paid || 0}</p>
                  </div>
                </div>
              </div>

              {/* Payment Options */}
              <div className="space-y-4">
                {/* Deposit Option */}
                <div className="bg-blue-900/20 border border-blue-600/30 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <Banknote className="h-5 w-5 text-blue-400" />
                    <h3 className="text-blue-400 font-semibold">{t('admin_deposit_advance')}</h3>
                  </div>
                  <div className="flex gap-2">
                    <Input
                      type="number"
                      value={depositAmount}
                      onChange={(e) => setDepositAmount(e.target.value)}
                      placeholder={t('admin_deposit_amount')}
                      className="bg-zinc-800 border-gray-700 text-white"
                    />
                    <Button
                      onClick={() => updatePaymentStatus('deposit', parseFloat(depositAmount) || 0)}
                      className="bg-blue-600 hover:bg-blue-500 whitespace-nowrap"
                      disabled={!depositAmount || parseFloat(depositAmount) <= 0}
                    >
                      {t('admin_register_deposit')}
                    </Button>
                  </div>
                </div>

                {/* Full Payment Option */}
                <Button
                  onClick={() => updatePaymentStatus('paid', selectedBooking.total_price)}
                  className="w-full bg-green-700 hover:bg-green-600 py-6"
                >
                  <CheckCircle className="h-5 w-5 mr-2" />
                  {t('admin_mark_fully_paid')} (€{selectedBooking.total_price})
                </Button>

                {/* Remove Payment Option */}
                {selectedBooking.payment_status !== 'unpaid' && (
                  <Button
                    onClick={() => updatePaymentStatus('unpaid', 0)}
                    variant="outline"
                    className="w-full border-gray-600 text-gray-400 hover:text-white"
                  >
                    <X className="h-4 w-4 mr-2" />
                    {t('admin_remove_payment')}
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </main>
    </div>
  );
};

export default AdminDashboard;
